import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Stack,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { Link } from 'react-router-dom';
import { getInventoryValuation } from '../firebase/inventoryCostServices';
import { formatCurrency } from '../utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inventory-tabpanel-${index}`}
      aria-labelledby={`inventory-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `inventory-tab-${index}`,
    'aria-controls': `inventory-tabpanel-${index}`,
  };
};

interface InventoryItem {
  productId: string;
  productName: string;
  productSku: string;
  quantity: number;
  averageCost: number;
  totalValue: number;
}

const InventoryDashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [excludeZeroStock, setExcludeZeroStock] = useState<boolean>(false);
  
  // Date filter states
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth());
  const [inventoryData, setInventoryData] = useState<{
    totalCost: number;
    items: InventoryItem[];
  }>({ totalCost: 0, items: [] });
  
  // Fetch inventory data
  useEffect(() => {
    const fetchInventoryData = async () => {
      setLoading(true);
      try {
        const data = await getInventoryValuation(selectedYear, selectedMonth);
        setInventoryData(data);
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInventoryData();
  }, [selectedYear, selectedMonth]);
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Handle year change
  const handleYearChange = (event: SelectChangeEvent) => {
    setSelectedYear(Number(event.target.value));
  };
  
  // Handle month change
  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(Number(event.target.value));
  };
  
  // Handle clear filters
  const handleClearFilters = () => {
    setSelectedYear(currentDate.getFullYear());
    setSelectedMonth(currentDate.getMonth());
    setSearchQuery('');
    setExcludeZeroStock(false);
  };
  
  // Filter inventory items based on search query and zero stock filter
  const filteredItems = inventoryData.items.filter(item => {
    // Apply search filter
    const matchesSearch = 
      item.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.productSku.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Apply zero stock filter if enabled
    const matchesZeroStock = excludeZeroStock ? item.quantity > 0 : true;
    
    return matchesSearch && matchesZeroStock;
  });
  
  // Get low stock items (for example, items with quantity < 10)
  const lowStockItems = inventoryData.items.filter(item => item.quantity < 10);
  
  // Get high value items (top 10 by value)
  const highValueItems = [...inventoryData.items]
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 10);
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Inventory Dashboard
        </Typography>
      </Box>
      
      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InventoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Total Inventory Value
                </Typography>
              </Box>
              <Typography variant="h4" component="div" fontWeight={600}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  formatCurrency(inventoryData.totalCost)
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Based on average cost
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CategoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Total Products
                </Typography>
              </Box>
              <Typography variant="h4" component="div" fontWeight={600}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  inventoryData.items.length
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique product SKUs
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 300px' }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Low Stock Items
                </Typography>
              </Box>
              <Typography variant="h4" component="div" fontWeight={600}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  lowStockItems.length
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items below reorder level
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      
      {/* Tabs and Content */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="inventory tabs">
            <Tab label="All Inventory" {...a11yProps(0)} />
            <Tab label="Low Stock" {...a11yProps(1)} />
            <Tab label="High Value" {...a11yProps(2)} />
          </Tabs>
        </Box>
        
        {/* All Inventory Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h6" component="div">
              All Inventory Items
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              {/* Date Filters */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Time Range
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value="monthly"
                    disabled
                    size="small"
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Year
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedYear.toString()}
                    onChange={handleYearChange}
                    size="small"
                  >
                    {[2023, 2024, 2025, 2026, 2027].map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Month
                </Typography>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedMonth.toString()}
                    onChange={handleMonthChange}
                    size="small"
                  >
                    {[
                      { value: 0, label: 'January' },
                      { value: 1, label: 'February' },
                      { value: 2, label: 'March' },
                      { value: 3, label: 'April' },
                      { value: 4, label: 'May' },
                      { value: 5, label: 'June' },
                      { value: 6, label: 'July' },
                      { value: 7, label: 'August' },
                      { value: 8, label: 'September' },
                      { value: 9, label: 'October' },
                      { value: 10, label: 'November' },
                      { value: 11, label: 'December' }
                    ].map(month => (
                      <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
              
              <TextField
                placeholder="Search inventory..."
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
              <Button
                variant={excludeZeroStock ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => setExcludeZeroStock(!excludeZeroStock)}
                sx={{ mr: 2 }}
              >
                {excludeZeroStock ? "Show All" : "Exclude Zero Stock"}
              </Button>
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Average Cost</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No inventory items found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow key={item.productId}>
                      <TableCell>{item.productSku}</TableCell>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell align="right">
                        {item.quantity < 10 ? (
                          <Chip 
                            label={item.quantity} 
                            size="small" 
                            color="warning" 
                            variant="outlined" 
                          />
                        ) : (
                          item.quantity
                        )}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalValue)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* Low Stock Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" component="div">
              Low Stock Items
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Items with quantity below reorder level
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Average Cost</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : lowStockItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No low stock items found
                    </TableCell>
                  </TableRow>
                ) : (
                  lowStockItems.map((item) => (
                    <TableRow key={item.productId}>
                      <TableCell>{item.productSku}</TableCell>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={item.quantity} 
                          size="small" 
                          color="warning" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalValue)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
        
        {/* High Value Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" component="div">
              High Value Items
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Top 10 items by total value
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SKU</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Average Cost</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={40} />
                    </TableCell>
                  </TableRow>
                ) : highValueItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      No inventory items found
                    </TableCell>
                  </TableRow>
                ) : (
                  highValueItems.map((item) => (
                    <TableRow key={item.productId}>
                      <TableCell>{item.productSku}</TableCell>
                      <TableCell>{item.productName}</TableCell>
                      <TableCell align="right">
                        {item.quantity < 10 ? (
                          <Chip 
                            label={item.quantity} 
                            size="small" 
                            color="warning" 
                            variant="outlined" 
                          />
                        ) : (
                          item.quantity
                        )}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalValue)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="primary">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default InventoryDashboard;
